import React, { cloneElement, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { usePortalContainer } from "./portalContainer";

export interface TooltipProps {
  title: string;
  placement?: "top" | "right" | "bottom";
  children: React.ReactElement;
}

/** Chain our overlay handler after any handler the child already defines, so
 *  wrapping a component in Tooltip never silently discards its own logic. */
function chain<E extends React.SyntheticEvent>(
  theirs: unknown,
  ours: (e: E) => void,
): (e: E) => void {
  return (e) => {
    if (typeof theirs === "function") theirs(e);
    ours(e);
  };
}

/** Minimal portal tooltip: fixed-position so overflow-hidden ancestors (the
 *  sidebar) can't clip it. Hover + keyboard focus both trigger it. */
export function Tooltip({ title, placement = "top", children }: TooltipProps) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const portalTarget = usePortalContainer();

  const show = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    if (placement === "right") setPos({ x: r.right + 8, y: r.top + r.height / 2 });
    else if (placement === "bottom") setPos({ x: r.left + r.width / 2, y: r.bottom + 8 });
    else setPos({ x: r.left + r.width / 2, y: r.top - 8 });
  };
  const hide = () => setPos(null);

  const childProps = (children as React.ReactElement<Record<string, unknown>>).props;
  const child = cloneElement(children as React.ReactElement<Record<string, unknown>>, {
    onMouseEnter: chain<React.MouseEvent<HTMLElement>>(childProps.onMouseEnter, (e) => show(e.currentTarget)),
    onMouseLeave: chain<React.MouseEvent<HTMLElement>>(childProps.onMouseLeave, hide),
    onFocus: chain<React.FocusEvent<HTMLElement>>(childProps.onFocus, (e) => show(e.currentTarget)),
    onBlur: chain<React.FocusEvent<HTMLElement>>(childProps.onBlur, hide),
    // Acting on the trigger dismisses the tooltip. Without this, a trigger that
    // swaps or moves on click (the sidebar collapse button) never receives
    // mouseleave, so the tooltip stays stuck over the page at a stale position.
    onPointerDown: chain<React.PointerEvent<HTMLElement>>(childProps.onPointerDown, hide),
    onClick: chain<React.MouseEvent<HTMLElement>>(childProps.onClick, hide),
  });

  const transform =
    placement === "right" ? "translate(0, -50%)"
    : placement === "bottom" ? "translate(-50%, 0)"
    : "translate(-50%, -100%)";

  return (
    <>
      {child}
      {pos !== null &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: "fixed", left: pos.x, top: pos.y, transform, zIndex: 70 }}
            className={cn(
              "pointer-events-none rounded-md bg-ink px-2 py-1 text-xs font-medium",
              "text-canvas whitespace-nowrap shadow-md",
            )}
          >
            {title}
          </div>,
          portalTarget,
        )}
    </>
  );
}
